/**
 * Menu slug path utilities
 * Supports URL scheme: {restaurant-slug}.restohub.io/{lang?}/{menu-slug}/...
 *
 * Language prefix is optional: /pickup-menu and /en/pickup-menu both resolve
 * to English. Non-English languages require a prefix: /fr/pickup-menu.
 */

/** Supported UI languages */
export const SUPPORTED_LANGS = new Set(['en', 'fr', 'es'])

/** Paths that are routes, not menu slugs */
const RESERVED_PATHS = new Set([
  'tracking',
  'order',
  'checkout',
  'cart',
  'menu',
])

/**
 * Extract language prefix from path.
 *
 * /fr/pickup-menu     → { lang: 'fr', pathWithoutLang: '/pickup-menu' }
 * /en/pickup-menu     → { lang: 'en', pathWithoutLang: '/pickup-menu' }
 * /pickup-menu        → { lang: 'en', pathWithoutLang: '/pickup-menu' }
 * /fr                 → { lang: 'fr', pathWithoutLang: '/' }
 * /                   → { lang: 'en', pathWithoutLang: '/' }
 */
export function parseLangFromPath(pathname: string): { lang: string; pathWithoutLang: string } {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]?.toLowerCase() || ''
  if (SUPPORTED_LANGS.has(first)) {
    const rest = '/' + segments.slice(1).join('/')
    return { lang: first, pathWithoutLang: rest }
  }
  return { lang: 'en', pathWithoutLang: pathname }
}

/**
 * Extract menu slug from first path segment.
 * NOTE: call this on pathWithoutLang (after stripping lang prefix).
 *
 * /summer-menu/menu → 'summer-menu'
 * /summer-menu     → 'summer-menu'
 * /tracking/ABC    → '' (reserved path, not a slug)
 * /                → ''
 */
export function parseMenuSlugFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0] || ''
  if (RESERVED_PATHS.has(first)) return ''
  return first
}

/**
 * Strip menu slug prefix from pathname to get the sub-route.
 * /summer-menu/menu → '/menu'
 * /summer-menu      → '/'
 * / (no slug)       → '/'
 */
export function stripMenuSlugPrefix(pathname: string, slug: string): string {
  if (!slug) return pathname
  const prefix = `/${slug}`
  if (pathname === prefix) return '/'
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length)
  return '/'
}
